import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, CheckCircle2, MessageCircle, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import Contact from "@/components/sections/Contact";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { COURSES } from "@/lib/courses";
import { COMPANY } from "@/lib/data";

const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

export default function CoursePage() {
  const { slug } = useParams();
  const course = COURSES[slug];

  useEffect(() => {
    if (!course) return;
    document.title = course.title;

    const setMeta = (name, content, attr = "name") => {
      let m = document.querySelector(`meta[${attr}="${name}"]`);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute(attr, name);
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
    };

    setMeta("description", course.seo_desc);
    setMeta("keywords", course.seo_keywords || "");
    setMeta("og:title", course.title, "property");
    setMeta("og:description", course.seo_desc, "property");
    setMeta("og:image", course.hero_image, "property");
    setMeta("og:type", "website", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", course.title);
    setMeta("twitter:description", course.seo_desc);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = `${window.location.origin}/courses/${course.slug}`;

    // Course + LocalBusiness schema
    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Course",
          name: course.title,
          description: course.seo_desc,
          provider: {
            "@type": "EducationalOrganization",
            name: "Globe Vista Overseas Education",
            sameAs: window.location.origin,
          },
          educationalLevel: "Professional",
          teaches: course.code,
          hasCourseInstance: course.pricing.map((p) => ({
            "@type": "CourseInstance",
            name: `${course.code} — ${p.name}`,
            courseMode: "Blended",
            location: {
              "@type": "Place",
              name: "Globe Vista, Santosh Nagar, Hyderabad",
            },
          })),
        },
        {
          "@type": "LocalBusiness",
          name: "Globe Vista Overseas Education",
          image: course.hero_image,
          telephone: "+917416807560",
          email: "Globevistaoverseasedu@gmail.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Santosh Nagar",
            addressLocality: "Hyderabad",
            addressRegion: "Telangana",
            postalCode: "500059",
            addressCountry: "IN",
          },
          areaServed: ["Hyderabad", "Santosh Nagar", "Telangana"],
          knowsAbout: (course.seo_keywords || "").split(",").map((s) => s.trim()).filter(Boolean),
          priceRange: "₹6,000 - ₹28,000",
        },
      ],
    };
    let s = document.getElementById("gv-course-schema");
    if (!s) {
      s = document.createElement("script");
      s.id = "gv-course-schema";
      s.type = "application/ld+json";
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify(schema);
  }, [course]);

  if (!course) return <Navigate to="/" replace />;

  const waUrl = `https://wa.me/${COMPANY.phoneRaw.replace("+", "")}?text=${encodeURIComponent(
    `Hi Globe Vista, I'd like to enrol in ${course.code} coaching. Please share the next batch details.`
  )}`;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={`course-page-${course.slug}`}>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[90vh] flex items-center overflow-hidden pt-32 pb-20">
          <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#050B1A] via-[#0A2160] to-[#0F3D8C]">
            <img src={course.hero_image} alt={course.title} className="w-full h-full object-cover opacity-55" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#050B1A]/85 via-[#0A2160]/75 to-[#0F3D8C]/65" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(212,175,55,0.35),_transparent_55%)]" />
          </div>

          <div className="max-w-7xl mx-auto px-6 md:px-8 w-full relative z-10 text-white">
            <div className="max-w-3xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-xs tracking-[0.2em] uppercase"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#FCD34D]" />
                {course.code} · Test Preparation
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95]"
              >
                {course.hero_title} <br />
                <span className="font-serif italic text-gradient-gold">{course.hero_accent}</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.25 }}
                className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed"
              >
                {course.hero_sub}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="mt-8 flex flex-wrap gap-3"
              >
                <Button
                  data-testid={`${course.slug}-enrol-btn`}
                  onClick={() => scrollTo("#contact")}
                  className="rounded-full bg-[#D4AF37] hover:bg-[#B99328] text-[#0F172A] font-semibold px-7 py-6 shadow-xl shadow-[#D4AF37]/30 group"
                >
                  Enrol Now
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${course.slug}-wa-btn`}
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold px-7 py-3.5 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp for Batches
                </a>
                <Link
                  to="/#test-prep"
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/30 text-white hover:bg-white/20 px-7 py-3.5 font-medium"
                >
                  All Tests
                </Link>
              </motion.div>

              {/* Highlights */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.55 }}
                className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl"
              >
                {course.highlights.map((h) => {
                  const Icon = Icons[h.icon] || Icons.Sparkles;
                  return (
                    <div key={h.label} className="p-4 rounded-2xl bg-white/[0.06] backdrop-blur-xl border border-white/15">
                      <Icon className="w-5 h-5 text-[#FCD34D]" />
                      <div className="mt-2 text-2xl font-heading font-light">{h.value}</div>
                      <div className="text-[11px] uppercase tracking-[0.15em] text-white/60">{h.label}</div>
                    </div>
                  );
                })}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Modules / Curriculum */}
        <section className="py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-4">Curriculum</div>
                <h2 className="text-4xl sm:text-5xl font-light tracking-tighter">
                  Every skill, <span className="font-serif italic text-gradient-royal">mastered.</span>
                </h2>
              </div>
              <p className="text-muted-foreground max-w-md">
                A structured, section-wise curriculum designed by certified {course.code} trainers.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {course.modules.map((m, i) => (
                <motion.div
                  key={m.section}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`${course.slug}-module-${i}`}
                  className="p-8 rounded-3xl bg-card border border-border hover:border-[#0F3D8C]/50 dark:hover:border-[#2563EB]/50 hover:-translate-y-1 transition-all group"
                >
                  <div className="flex items-start justify-between">
                    <Badge className="bg-[#0F3D8C]/10 dark:bg-[#2563EB]/20 text-[#0F3D8C] dark:text-[#60A5FA] hover:bg-[#0F3D8C]/10">
                      Module {String(i + 1).padStart(2, "0")}
                    </Badge>
                    <div className="text-4xl font-serif font-light text-[#0F3D8C]/10 dark:text-white/10">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <h3 className="mt-4 font-heading font-semibold text-xl">{m.section}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-24 md:py-32 bg-secondary/30 dark:bg-secondary/10">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-4">
                  Why Globe Vista {course.code}
                </div>
                <h2 className="text-4xl sm:text-5xl font-light tracking-tighter">
                  A program built for <span className="font-serif italic text-gradient-royal">real results.</span>
                </h2>
                <p className="mt-4 text-muted-foreground text-lg">
                  Every element of our {course.code} training has been reverse-engineered from 500+ successful student journeys.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                {course.features.map((f, i) => (
                  <motion.div
                    key={f}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    data-testid={`${course.slug}-feature-${i}`}
                    className="flex items-start gap-3 p-4 rounded-2xl bg-card border border-border"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#0F3D8C] dark:text-[#60A5FA] shrink-0 mt-0.5" />
                    <span className="text-sm">{f}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SEO Local content block */}
        <section className="py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-6 md:px-8 text-center">
            <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-4">Serving Hyderabad</div>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tighter">
              {course.seo_h2}
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">{course.seo_para}</p>

            <div className="mt-8 flex flex-wrap justify-center gap-2 text-xs">
              {(course.seo_keywords || "")
                .split(",")
                .map((k) => k.trim())
                .filter(Boolean)
                .map((k) => (
                  <span
                    key={k}
                    className="px-3 py-1.5 rounded-full bg-secondary/60 border border-border text-muted-foreground"
                  >
                    {k}
                  </span>
                ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-4">Batches & Pricing</div>
              <h2 className="text-4xl sm:text-5xl font-light tracking-tighter">
                Choose your <span className="font-serif italic text-gradient-royal">learning plan.</span>
              </h2>
              <p className="mt-4 text-muted-foreground">Transparent, all-inclusive pricing. No hidden charges.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {course.pricing.map((p, i) => (
                <motion.div
                  key={p.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`${course.slug}-plan-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative p-8 rounded-3xl border transition-all ${
                    p.popular
                      ? "border-[#D4AF37] shadow-2xl shadow-[#D4AF37]/10 scale-[1.02] bg-card"
                      : "border-border bg-card hover:-translate-y-1"
                  }`}
                >
                  {p.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0F172A] text-[10px] font-bold tracking-[0.2em] uppercase px-4 py-1 rounded-full">
                      Most Popular
                    </div>
                  )}
                  <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">{p.name}</div>
                  <div className="mt-3 text-5xl font-heading font-light tracking-tight">{p.price}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{p.duration}</div>
                  <ul className="mt-6 space-y-2.5">
                    {p.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#0F3D8C] dark:text-[#60A5FA] shrink-0 mt-0.5" />
                        {perk}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => scrollTo("#contact")}
                    className={`mt-8 w-full rounded-full ${
                      p.popular
                        ? "bg-[#D4AF37] hover:bg-[#B99328] text-[#0F172A] font-semibold"
                        : "bg-[#0F3D8C] hover:bg-[#0C3275] dark:bg-[#2563EB] dark:hover:bg-[#3B82F6] text-white"
                    }`}
                    data-testid={`${course.slug}-plan-cta-${i}`}
                  >
                    Enrol Now
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-24 md:py-32 bg-secondary/30 dark:bg-secondary/10">
          <div className="max-w-4xl mx-auto px-6 md:px-8">
            <div className="text-center mb-12">
              <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-4">{course.code} FAQs</div>
              <h2 className="text-4xl sm:text-5xl font-light tracking-tighter">
                Your questions, <span className="font-serif italic text-gradient-royal">answered.</span>
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {course.faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`${course.slug}-faq-${i}`}
                  className="border border-border rounded-2xl px-5 bg-card data-[state=open]:border-[#D4AF37]/60 data-[state=open]:shadow-md transition-all"
                >
                  <AccordionTrigger className="text-left font-heading font-medium text-base sm:text-lg hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Contact reuse */}
        <Contact />
      </main>
      <Footer />
      <FloatingActions />
    </div>
  );
}
